import React, { useEffect, useRef } from "react";
import { createPortal } from "react-dom";

export interface ModalProps {
  open: boolean;
  /** Rendered as the dialog heading and used as its accessible name. */
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  /** Action row pinned to the bottom right of the panel. */
  footer?: React.ReactNode;
  /**
   * Drops the heading bar, leaving the panel to whatever `children` draws —
   * for a dialog whose whole message is its body, like a confirmation that
   * leads with an icon. The title still names the dialog to a screen reader,
   * so it is never a reason to leave it off.
   */
  hideTitle?: boolean;
  /**
   * A heading bar of the caller's own, drawn where `title` would be and kept
   * out of the scrolling body. For a heading that is more than text — a name
   * with a status pill beside it — which `title` cannot be, since it doubles
   * as the accessible name. Pair it with `hideTitle`, and the dialog is still
   * named to a screen reader by `title`.
   */
  header?: React.ReactNode;
  /**
   * Scrolls the body rather than the whole panel, which keeps the heading and
   * the action row in place while the content moves under them.
   *
   * Off by default: a short dialog that grows past the viewport only on a
   * small screen is better off scrolling whole, and pinning a two-button
   * footer to a panel that barely fills the screen only costs it a rule it
   * doesn't need. Turn it on for a panel that is long by nature — a form or a
   * submission read end to end — where the buttons would otherwise sit a
   * screenful below the last thing the reader looked at.
   *
   * The panel takes its height from `className`; without one it still grows to
   * the viewport and scrolls from there.
   */
  scrollBody?: boolean;
  /**
   * Whether a click on the backdrop closes the dialog. On by default, which is
   * what an ordinary dialog should do.
   *
   * Turn it off for one that is asking something rather than showing something
   * — a first-run prompt with two real answers in it, where a stray click
   * beside the panel is far more likely to be a miss than a decision, and
   * answering it for them loses the prompt for the rest of the session. Escape
   * still closes either way: that one is deliberate, and a modal that cannot be
   * escaped is a trap for anyone on a keyboard.
   *
   * Not a way to force an answer. Every dialog needs a way out that does not
   * commit to anything, and switching this off means the panel has to carry
   * that itself.
   */
  dismissOnBackdrop?: boolean;
  /** Layout override for the panel, e.g. a wider `max-w-*`. */
  className?: string;
}

/**
 * Centred dialog rendered into `document.body`. The portal matters: the shell
 * pages are `overflow-hidden` flex columns, so a dialog left inside the tree
 * would be clipped by whichever card it was opened from.
 */
const Modal: React.FC<ModalProps> = ({
  open,
  title,
  onClose,
  children,
  footer,
  hideTitle = false,
  header,
  scrollBody = false,
  dismissOnBackdrop = true,
  className = "max-w-[526px]",
}) => {
  const panelRef = useRef<HTMLDivElement>(null);
  // Whatever had focus before the dialog opened, so it can be handed back on
  // close instead of dropping focus to the top of the document.
  const opener = useRef<HTMLElement | null>(null);

  // Escape is read through a ref rather than closed over, so the effect below
  // can depend on `open` alone.
  //
  // This is not a micro-optimisation. Callers pass `onClose` as an inline arrow
  // — most of them have to, since it usually guards on something ("not while
  // this is saving") — so its identity changes on every render. An effect
  // depending on it therefore tears down and sets up again on every keystroke
  // inside the dialog, and its cleanup hands focus back to whatever opened it.
  // A dialog with a text box in it then takes exactly one character before the
  // caret is pulled out of the field.
  const onCloseRef = useRef(onClose);
  useEffect(() => {
    onCloseRef.current = onClose;
  });

  useEffect(() => {
    if (!open) return;

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onCloseRef.current();
    };

    opener.current = document.activeElement as HTMLElement | null;
    document.addEventListener("keydown", handleKeyDown);

    // The page behind must not scroll under the backdrop.
    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";

    // Focus the panel rather than the first control: the heading should be
    // what a screen reader announces first. Once per opening — anything the
    // user has focused since is theirs, and stealing it back mid-typing is the
    // bug this effect used to have.
    const panel = panelRef.current;
    panel?.focus();

    return () => {
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = overflow;

      // Only once the dialog is really gone. In development React runs this
      // cleanup and then sets the effect up again, with the panel still on
      // screen — and handing focus back to the opener while the dialog is
      // still up is not a no-op: a control that opens something when it is
      // focused (the dish field's own list) opens it, over the top of the
      // dialog. The panel is off the document by the time a true unmount
      // reaches here, which is what tells the two apart.
      if (!panel?.isConnected) opener.current?.focus();
    };
  }, [open]);

  if (!open) return null;

  const titleId = "modal-title";

  return createPortal(
    // The overlay closes on a click that both starts and ends on itself, so a
    // drag that begins inside the panel and releases outside doesn't dismiss.
    // Unless the dialog has asked not to be — see `dismissOnBackdrop`.
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-[#0B1B1F]/40 px-4 py-6"
      onMouseDown={(event) => {
        if (!dismissOnBackdrop) return;
        if (event.target === event.currentTarget) onClose();
      }}
    >
      {/* When the body scrolls, the panel itself must not: it becomes the
          column that hands its leftover height to the body, and the clipping
          is what keeps the content off the rounded corners. */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        tabIndex={-1}
        className={`w-full ${className} max-h-full rounded-lg bg-white shadow-[0_16px_48px_rgba(11,27,31,0.18)] focus:outline-none ${
          scrollBody ? "flex flex-col overflow-hidden" : "overflow-y-auto"
        }`}
      >
        <h2
          id={titleId}
          className={
            hideTitle
              ? "sr-only"
              : "shrink-0 border-b border-line px-6 py-5 text-[17px] 3xl:text-[19px] font-medium text-ink"
          }
        >
          {title}
        </h2>

        {header && (
          <div className="shrink-0 border-b border-line px-6 py-4">{header}</div>
        )}

        <div
          className={`px-6 py-5 ${
            scrollBody
              ? "min-h-0 flex-1 overflow-y-auto custom-scrollbar-overlay"
              : ""
          }`}
        >
          {children}
        </div>

        {footer && (
          <div
            className={`flex items-center justify-end gap-3 px-6 ${
              scrollBody ? "shrink-0 border-t border-line py-4" : "pb-6"
            }`}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
};

export default Modal;
